// utils/performance.js - Performance monitoring utilities (Fixed)
import React from 'react';
import logger from './logger';

class PerformanceMonitor {
  constructor() {
    this.metrics = new Map();
    this.observers = new Map();
    this.isEnabled = process.env.NODE_ENV === 'development' || 
                     localStorage.getItem('enablePerformanceMonitoring') === 'true';
  }

  // Measure API call performance
  async measureApiCall(name, apiCall) {
    if (!this.isEnabled) return await apiCall();

    const start = performance.now();
    try {
      const result = await apiCall();
      const duration = performance.now() - start;
      
      this.recordMetric(`api_${name}`, duration);
      logger.performance(`API ${name}`, duration);
      
      return result;
    } catch (error) {
      const duration = performance.now() - start;
      logger.error(`API ${name} failed after ${duration.toFixed(2)}ms:`, error);
      throw error;
    }
  }

  // Measure component render time
  measureRender(componentName) {
    if (!this.isEnabled) {
      return (WrappedComponent) => WrappedComponent;
    }

    return (WrappedComponent) => {
      return function MeasuredComponent(props) {
        const renderStart = performance.now();
        
        React.useEffect(() => {
          const renderTime = performance.now() - renderStart;
          
          if (renderTime > 16) { // Longer than 1 frame (60fps)
            logger.performance(`${componentName} render`, renderTime);
          }
          
          this.recordMetric(`render_${componentName}`, renderTime);
        });

        return React.createElement(WrappedComponent, props);
      }.bind(this);
    };
  }

  // Measure function execution time
  measureFunction(name, fn) {
    if (!this.isEnabled) return fn;

    return (...args) => {
      const start = performance.now();
      try {
        const result = fn(...args);
        const duration = performance.now() - start;
        
        if (duration > 10) { // Only log if > 10ms
          logger.performance(`Function ${name}`, duration);
        }
        
        this.recordMetric(`function_${name}`, duration);
        return result;
      } catch (error) {
        const duration = performance.now() - start;
        logger.error(`Function ${name} failed after ${duration.toFixed(2)}ms:`, error);
        throw error;
      }
    };
  }

  // Record metric for analysis
  recordMetric(name, value) {
    if (!this.metrics.has(name)) {
      this.metrics.set(name, []);
    }
    
    const values = this.metrics.get(name);
    values.push({
      value,
      timestamp: Date.now()
    });
    
    // Keep only last 100 measurements
    if (values.length > 100) {
      values.shift();
    }
  }

  // Get performance statistics
  getStats(name) {
    const values = this.metrics.get(name);
    if (!values || values.length === 0) return null;

    const nums = values.map(v => v.value);
    const sum = nums.reduce((a, b) => a + b, 0);
    const avg = sum / nums.length;
    const min = Math.min(...nums);
    const max = Math.max(...nums);
    
    // Calculate percentiles
    const sorted = [...nums].sort((a, b) => a - b);
    const p50 = sorted[Math.floor(sorted.length * 0.5)];
    const p90 = sorted[Math.floor(sorted.length * 0.9)];
    const p95 = sorted[Math.floor(sorted.length * 0.95)];

    return {
      count: nums.length,
      avg: Math.round(avg * 100) / 100,
      min: Math.round(min * 100) / 100,
      max: Math.round(max * 100) / 100,
      p50: Math.round(p50 * 100) / 100,
      p90: Math.round(p90 * 100) / 100,
      p95: Math.round(p95 * 100) / 100
    };
  }

  // Get all performance metrics
  getAllStats() {
    const stats = {};
    for (const [name] of this.metrics) {
      stats[name] = this.getStats(name);
    }
    return stats;
  }

  // Monitor memory usage
  monitorMemory() {
    if (!this.isEnabled || !performance.memory) return;

    const memory = performance.memory;
    const memoryInfo = {
      used: Math.round(memory.usedJSHeapSize / 1024 / 1024), // MB
      total: Math.round(memory.totalJSHeapSize / 1024 / 1024), // MB
      limit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024) // MB
    };

    this.recordMetric('memory_used', memoryInfo.used);
    this.recordMetric('memory_total', memoryInfo.total);

    // Warn if memory usage is high
    const usagePercent = (memoryInfo.used / memoryInfo.limit) * 100;
    if (usagePercent > 80) {
      logger.warn(`High memory usage: ${memoryInfo.used}MB (${usagePercent.toFixed(1)}%)`);
    }

    return memoryInfo;
  }

  // Monitor long tasks (blocking the main thread)
  monitorLongTasks() {
    if (!this.isEnabled || !window.PerformanceObserver) return;

    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.duration > 50) { // Tasks longer than 50ms
            logger.warn(`Long task detected: ${entry.duration.toFixed(2)}ms`);
            this.recordMetric('long_task', entry.duration);
          }
        }
      });

      observer.observe({ entryTypes: ['longtask'] });
      this.observers.set('longtask', observer);
    } catch (error) {
      logger.error('Failed to set up long task monitoring:', error);
    }
  }

  // Monitor largest contentful paint
  monitorLCP() {
    if (!this.isEnabled || !window.PerformanceObserver) return;

    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          logger.performance('Largest Contentful Paint', entry.startTime);
          this.recordMetric('lcp', entry.startTime);
        }
      });

      observer.observe({ entryTypes: ['largest-contentful-paint'] });
      this.observers.set('lcp', observer);
    } catch (error) {
      logger.error('Failed to set up LCP monitoring:', error);
    }
  }

  // Start all monitoring
  startMonitoring() {
    if (!this.isEnabled) return;

    logger.info('Starting performance monitoring');
    
    this.monitorLongTasks();
    this.monitorLCP();
    
    // Monitor memory every 30 seconds
    setInterval(() => {
      this.monitorMemory();
    }, 30000);
  }

  // Stop all monitoring
  stopMonitoring() {
    for (const [, observer] of this.observers) {
      observer.disconnect();
    }
    this.observers.clear();
    logger.info('Stopped performance monitoring');
  }

  // Generate performance report
  generateReport() {
    const stats = this.getAllStats();
    const memory = this.monitorMemory();
    
    const report = {
      timestamp: new Date().toISOString(),
      memory,
      metrics: stats,
      summary: {
        slowestApi: this.findSlowestMetric('api_'),
        slowestRender: this.findSlowestMetric('render_'),
        slowestFunction: this.findSlowestMetric('function_')
      }
    };

    logger.info('Performance Report:', report);
    return report;
  }

  // Find slowest metric by prefix
  findSlowestMetric(prefix) {
    let slowest = null;
    let maxAvg = 0;

    for (const [name, stats] of Object.entries(this.getAllStats())) {
      if (name.startsWith(prefix) && stats && stats.avg > maxAvg) {
        maxAvg = stats.avg;
        slowest = { name: name.replace(prefix, ''), avg: stats.avg };
      }
    }

    return slowest;
  }
}

// Create singleton instance
const performanceMonitor = new PerformanceMonitor();

// Auto-start monitoring in development
if (process.env.NODE_ENV === 'development') {
  performanceMonitor.startMonitoring();
}

export default performanceMonitor;

// Convenience exports
export const measureApiCall = performanceMonitor.measureApiCall.bind(performanceMonitor);
export const measureRender = performanceMonitor.measureRender.bind(performanceMonitor);
export const measureFunction = performanceMonitor.measureFunction.bind(performanceMonitor);
export const generateReport = performanceMonitor.generateReport.bind(performanceMonitor);